import { useContext, useState, useEffect, useRef } from 'react';
import handleChange from '../../../utils/handleChange';
import './orgLogin.css';
import { orgGetOTPFun, orgLoginFunAPI } from '../../../api/orgLoginAPI';
import { OrgAuthContext } from '../../../context/OrgAuthContext';
import { useNavigate } from 'react-router-dom';
import errorHandle from '../../../utils/errorHandle';
import ReactLoading from 'react-loading';
import { toast } from 'react-toastify';
import phoneValid from '../../../utils/phoneValid';
import OTPInput from '../../otpinput/otpinput';
import TrvToLogo from '../../../assets/images/trvto-logo.png';
import { ReactComponent as TravelImg } from '../../../assets/images/trvto_travel.svg';
import { ReactComponent as Announcement } from '../../../assets/images/trvto_announcement.svg';
import { ReactComponent as Phone } from '../../../assets/icons/trvto_phone.svg';
import { ReactComponent as Email } from '../../../assets/icons/trvto_email.svg';
import CustomDropdown from '../../reusable-ui-components/customDropdown/CustomDropdown';

const MAP_OPTION_TYPE = {
    trip: 't',
    act: 'a',
    gu: 'g',
    camp: 'c'
};

const OPTIONS = [
    { id: 'trip', label: 'Trip' },
    { id: 'act', label: 'Activity' },
    { id: 'gu', label: 'Guide' },
    { id: 'camp', label: 'Camp/Villa' }
];

const RESEND_DURATION = 120; // seconds
const OTP_EXPIRY_DURATION = 3 * 60 * 1000; // 3 minutes in ms

function OrgLogin({ setLoginSec }) {
    const navigate = useNavigate();
    const { orgLoginFun } = useContext(OrgAuthContext);

    const [userDetails, setUserDetails] = useState({ phoneNumber: '', otp: '' });
    const [selectedOption, setSelectedOption] = useState(OPTIONS[0]);
    const [loading, setLoading] = useState(false);

    // 'initial' | 'sent'
    const [otpState, setOtpState] = useState('initial');
    const [otpExpired, setOtpExpired] = useState(false);

    const [phoneError, setPhoneError] = useState('');
    const [otpError, setOtpError] = useState('');
    const [resendTimer, setResendTimer] = useState(0);

    // Refs so closures always see the latest values
    const resendIntervalRef = useRef(null);
    const otpExpiryTimeoutRef = useRef(null);

    // Cleanup on unmount
    useEffect(() => {
        return () => {
            clearInterval(resendIntervalRef.current);
            clearTimeout(otpExpiryTimeoutRef.current);
        };
    }, []);

    function startResendTimer(duration = RESEND_DURATION) {
        clearInterval(resendIntervalRef.current);
        setResendTimer(duration);

        resendIntervalRef.current = setInterval(() => {
            setResendTimer((prev) => {
                if (prev <= 1) {
                    clearInterval(resendIntervalRef.current);
                    resendIntervalRef.current = null;
                    return 0;
                }
                return prev - 1;
            });
        }, 1000);
    }

    function stopAllTimers() {
        clearInterval(resendIntervalRef.current);
        resendIntervalRef.current = null;
        clearTimeout(otpExpiryTimeoutRef.current);
        otpExpiryTimeoutRef.current = null;
        setResendTimer(0);
    }

    function scheduleOtpExpiry() {
        clearTimeout(otpExpiryTimeoutRef.current);

        otpExpiryTimeoutRef.current = setTimeout(() => {
            // Clear resend countdown too — it's no longer relevant
            clearInterval(resendIntervalRef.current);
            resendIntervalRef.current = null;
            setResendTimer(0);

            setOtpExpired(true);
            setOtpState('initial'); // hide OTP input
            setOtpError('OTP has expired. Please generate a new one.');
        }, OTP_EXPIRY_DURATION);
    }

    async function getOrgOTP(method) {
        if (!phoneValid(userDetails.phoneNumber)) {
            setPhoneError('Please enter a valid phone number');
            return;
        }

        setPhoneError('');
        setLoading(true);

        try {
            await orgGetOTPFun(selectedOption.id, method, {
                phoneNumber: userDetails.phoneNumber
            });

            toast.success('OTP sent to your WhatsApp');
            setOtpState('sent');
            setOtpExpired(false);
            setOtpError('');
            // Reset OTP field on resend
            setUserDetails((prev) => ({ ...prev, otp: '' }));

            startResendTimer(RESEND_DURATION);
            scheduleOtpExpiry();
        } catch (error) {
            errorHandle(error);
        } finally {
            setLoading(false);
        }
    }

    async function handleOrgLogin() {
        if (userDetails.otp.length < 6) {
            const msg = 'Please enter the complete OTP';
            toast.error(msg);
            setOtpError(msg);
            return;
        }

        setOtpError('');
        setLoading(true);

        try {
            const loginData = await orgLoginFunAPI(selectedOption.id, userDetails);
            toast.success('Login Successful');
            stopAllTimers();
            orgLoginFun(loginData?.data);
            navigate(`/org/${MAP_OPTION_TYPE[selectedOption.id]}/dashboard`);
        } catch (error) {
            errorHandle(error);
        } finally {
            setLoading(false);
        }
    }

    const handleKeyDown = (e) => {
        if (e.key !== 'Enter') return;

        if (otpState === 'initial' || otpExpired) {
            getOrgOTP(otpExpired ? 'resend' : 'login');
        } else {
            handleOrgLogin();
        }
    };

    const handleMainButtonClick = () => {
        if (otpState === 'initial' || otpExpired) {
            getOrgOTP(otpExpired ? 'resend' : 'login');
        } else {
            handleOrgLogin();
        }
    };

    const mainButtonLabel =
        otpState === 'initial' ? 'Send One Time Password' : otpExpired ? 'Generate New OTP' : 'Login';

    const formatTimer = (seconds) => {
        const m = Math.floor(seconds / 60);
        const s = String(seconds % 60).padStart(2, '0');
        return `${m}:${s}`;
    };

    return (
        <div>
            {loading && (
                <div className='loadingBackground'>
                    <ReactLoading type='spin' color='#006aff' className='loadingSpin' />
                </div>
            )}
            <div className='orgLogin'>
                <div>
                    <div className='orgLoginLogoDiv'>
                        <img src={TrvToLogo} alt='trvto_logo' className='orgLogoImg' loading='lazy' decoding='async' />
                    </div>
                    <div className='orgLoginDiv'>
                        <Announcement />
                        <h1 className='orgLoginH1'>Organizer Login</h1>

                        <div className='orgLoginInputDiv'>
                            <CustomDropdown
                                placeholder='Please select organizer login type'
                                options={OPTIONS}
                                value={selectedOption}
                                onChange={setSelectedOption}
                            />
                        </div>

                        <div className='orgLoginInputDiv'>
                            <input
                                placeholder='Organizer phone number'
                                name='phoneNumber'
                                onChange={(e) => handleChange(e, setUserDetails)}
                                onKeyDown={handleKeyDown}
                                value={userDetails.phoneNumber}
                                type='number'
                                minLength={10}
                                maxLength={10}
                                className='orgLoginInput'
                            />
                            {phoneError && <span className='orgLoginError'>{phoneError}</span>}

                            {otpState === 'sent' && (
                                <div className='loginInputDiv'>
                                    <p>Please check your WhatsApp for the OTP</p>
                                    <label>OTP</label>
                                    <OTPInput
                                        length={6}
                                        setUserData={setUserDetails}
                                        keyName='otp'
                                        handleKeyDown={handleKeyDown}
                                    />
                                </div>
                            )}

                            {otpState === 'sent' && !otpExpired && (
                                <div className='resend-container'>
                                    {resendTimer > 0 ? (
                                        <p className='resend-timer'>Resend OTP in {formatTimer(resendTimer)}</p>
                                    ) : (
                                        <button
                                            className='resend-btn'
                                            onClick={() => getOrgOTP('resend')}
                                            disabled={loading}
                                        >
                                            Resend OTP
                                        </button>
                                    )}
                                </div>
                            )}

                            {otpError && <span className='orgLoginError'>{otpError}</span>}
                        </div>

                        <button
                            type='button'
                            onClick={handleMainButtonClick}
                            disabled={loading}
                            className='orgLoginBtn'
                        >
                            {mainButtonLabel}
                        </button>
                    </div>
                </div>

                <div className='orgLoginInfoDiv'>
                    <TravelImg />
                    <h2>Want to partner with us? Contact Our team.</h2>
                    <div className='contact-details org-login orgContInfDiv'>
                        <div className='details-item'>
                            <div className='icon-con'>
                                <Phone />
                            </div>
                            <a href='tel:8088788874'>8088788874</a>
                        </div>
                        <div className='details-item'>
                            <div className='icon-con'>
                                <Email />
                            </div>
                            <a href='mailto:info@trvto.com'>info@trvto.com</a>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
}

export default OrgLogin;
